import { useCallback, useRef, useState } from 'react';
import { Logger } from '@saviynt/common';

import { useLazyGetServiceIdDetailsQuery } from '../data-layer/serviceIdentityApi';

/**
 * Fetches service identity details for a given service ID.
 *
 * @param serviceId - The ID of the service identity to fetch details for.
 * @returns An object containing `handleFetchSummary`, `isSummaryLoading`, and `isSummaryError`.
 */
const useGetServiceIdDetails = (serviceId: number | undefined) => {
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);

  // API hooks
  const [fetchSummary, { isError: isSummaryError }] =
    useLazyGetServiceIdDetailsQuery();

  // Refs
  const summaryAbortRef = useRef<((reason: string) => void) | null>(null);

  const handleFetchSummary = useCallback(async () => {
    setIsSummaryLoading(true);

    if (summaryAbortRef.current) {
      summaryAbortRef.current('New Search');
    }

    try {
      if (!serviceId) {
        throw new Error('No service ID provided');
      }

      const controller = fetchSummary(serviceId);

      summaryAbortRef.current = () => controller.abort();

      const response = await controller.unwrap();

      return response;
    } catch (error) {
      Logger.log('Failed to get details', error);

      return 'Failed to get service identity details';
    } finally {
      setIsSummaryLoading(false);
    }
  }, [fetchSummary, serviceId]);

  return {
    handleFetchSummary,
    isSummaryLoading,
    isSummaryError,
  };
};

// eslint-disable-next-line import/prefer-default-export
export { useGetServiceIdDetails };
